import type { FC } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useTheme } from '@mui/material'

import type { ConversationTurnEntry } from '@/index'
import { formatTimestamp } from '@/common/utils'
import { CopyButton } from '@/common/components'

import { MarkdownRenderer } from '../markdown'
import { FIXED_TIMESTAMP_SIZE } from '../shared'
import { DEFAULT_BUBBLE_STYLES } from './defaults'
import { createBubblePaperSx } from './styles'

interface UserInputBubbleProps {
  readonly event: ConversationTurnEntry
}

export const UserInputBubble: FC<UserInputBubbleProps> = ({ event }) => {
  const theme = useTheme()

  if (!event.userPrompt) {
    return null
  }

  return (
    <Box sx={userPromptBubbleContainerSx}>
      <Box
        sx={createBubblePaperSx(
          theme,
          theme.palette.info.main,
          'right',
          DEFAULT_BUBBLE_STYLES.bubbles.user,
        )}
      >
        <Box sx={userPromptContentSx}>
          <MarkdownRenderer content={event.userPrompt} />
        </Box>
        <Box className="bubble-footer" sx={footerSx}>
          <Box className="copy-button" sx={copyButtonContainerSx}>
            <CopyButton text={event.userPrompt} variant="small" />
          </Box>
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={userPromptTimestampSx}>
        {formatTimestamp(event.timestamp)}
      </Typography>
    </Box>
  )
}

const userPromptBubbleContainerSx: SxProps<Theme> = {
  width: '100%',
  mb: 2,
}

const userPromptContentSx: SxProps<Theme> = {
  mb: 0,
}

const footerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  mt: 0,
  maxHeight: 0,
  overflow: 'hidden',
  opacity: 0,
  transition: 'max-height 0.2s ease-in-out, opacity 0.2s ease-in-out, margin-top 0.2s ease-in-out',
}

const userPromptTimestampSx: SxProps<Theme> = {
  display: 'block',
  mt: 0.5,
  textAlign: 'right',
  fontSize: FIXED_TIMESTAMP_SIZE,
}

const copyButtonContainerSx: SxProps<Theme> = {
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
}
